import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { wishlist as wishlistApi } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistIds, setWishlistIds] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // Fetch wishlist IDs on mount (for quick checking)
  const fetchWishlistIds = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlistIds([]);
      return;
    }
    
    try {
      const response = await wishlistApi.getIds();
      setWishlistIds(response.data.product_ids || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    }
  }, [isAuthenticated]);

  // Fetch full wishlist with product details
  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      setWishlistItems([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await wishlistApi.getAll();
      setWishlistItems(response.data.items || []);
      setWishlistIds(response.data.items?.map(p => p.id) || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchWishlistIds();
  }, [fetchWishlistIds]);

  // Check if product is in wishlist
  const isInWishlist = useCallback((productId) => {
    return wishlistIds.includes(productId);
  }, [wishlistIds]);

  // Toggle wishlist (add/remove)
  const toggleWishlist = async (productId, productName) => {
    if (!isAuthenticated) {
      toast.error('Please login to save items');
      return;
    }

    const wasInWishlist = isInWishlist(productId);
    
    // Optimistic update
    if (wasInWishlist) {
      setWishlistIds(prev => prev.filter(id => id !== productId));
    } else {
      setWishlistIds(prev => [...prev, productId]);
    }

    try {
      if (wasInWishlist) {
        await wishlistApi.remove(productId);
        toast.success('Removed from wishlist');
      } else {
        await wishlistApi.add(productId);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      // Revert on error
      if (wasInWishlist) {
        setWishlistIds(prev => [...prev, productId]);
      } else {
        setWishlistIds(prev => prev.filter(id => id !== productId));
      }
      toast.error('Failed to update wishlist');
    }
  };

  // Add to wishlist
  const addToWishlist = async (productId) => {
    if (!isAuthenticated) {
      toast.error('Please login to save items');
      return;
    }

    if (isInWishlist(productId)) return;

    setWishlistIds(prev => [...prev, productId]);
    
    try {
      await wishlistApi.add(productId);
      toast.success('Added to wishlist');
    } catch (error) {
      setWishlistIds(prev => prev.filter(id => id !== productId));
      toast.error('Failed to add to wishlist');
    }
  };

  // Remove from wishlist
  const removeFromWishlist = async (productId) => {
    setWishlistIds(prev => prev.filter(id => id !== productId));
    setWishlistItems(prev => prev.filter(p => p.id !== productId));
    
    try {
      await wishlistApi.remove(productId);
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
      fetchWishlistIds();
    }
  };

  const value = {
    wishlistIds,
    wishlistItems,
    loading,
    isInWishlist,
    toggleWishlist,
    addToWishlist,
    removeFromWishlist,
    fetchWishlist,
    fetchWishlistIds,
    count: wishlistIds.length,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
